import type { ResCenterMigrationErrorKey } from 'src/components/ResCenterMigrationBanner/copy'

export type ResCenterMigrationBannerState = {
  eligible: boolean
  help_article_url?: string | null
  res_center_url?: string | null
  forgot_password_url?: string | null
  learn_more_clicked_at?: string | null
  try_it_attempted_at?: string | null
  try_it_succeeded_at?: string | null
}

export type ResCenterMigrationTryItResult = {
  status: string
  succeeded: boolean
  state: ResCenterMigrationBannerState
}

export type TryItOutcome =
  | { kind: 'navigate'; url: string }
  | { kind: 'emailed'; status: 'sent' | 'already_sent' }
  | { kind: 'error'; errorKey: ResCenterMigrationErrorKey }
  | { kind: 'ignored' }

const RECOVERABLE_STATUSES = new Set<ResCenterMigrationErrorKey>([
  'processing',
  'existing_user_unprovisioned',
  'company_id_required',
  'company_mismatch',
  'debtor_user',
  'missing_rescenter_role',
  'missing_email',
  'delivery_failed',
  'delivery_unconfirmed',
  'not_found',
  'unavailable',
])

export const isSafeHttpUrl = (value?: string | null): value is string => {
  if (!value) return false
  try {
    const { protocol } = new URL(value)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

export const getForgotPasswordUrl = (
  state?: ResCenterMigrationBannerState | null,
): string | null =>
  isSafeHttpUrl(state?.forgot_password_url) ? state.forgot_password_url : null

export const isBannerVisible = (
  state?: ResCenterMigrationBannerState | null,
): boolean => state?.eligible === true

export const shouldRenderBannerView = ({
  state,
  outcome,
  learnMoreFailed,
}: {
  state?: ResCenterMigrationBannerState | null
  outcome: TryItOutcome | null
  learnMoreFailed: boolean
}): boolean =>
  isBannerVisible(state) ||
  learnMoreFailed ||
  outcome?.kind === 'emailed' ||
  outcome?.kind === 'error'

export const isTryItUnlocked = (
  state?: ResCenterMigrationBannerState | null,
): boolean => isBannerVisible(state) && !!state?.learn_more_clicked_at

export const openLearnMore = async ({
  state,
  recordLearnMore,
  openUrl,
}: {
  state: ResCenterMigrationBannerState
  recordLearnMore: () => Promise<unknown>
  openUrl: (url: string) => void
}): Promise<{ ok: true } | { ok: false }> => {
  if (!isSafeHttpUrl(state.help_article_url)) return { ok: false }

  // Opened before the async call so the browser treats it as a user gesture.
  openUrl(state.help_article_url)

  if (state.learn_more_clicked_at) return { ok: true }

  try {
    await recordLearnMore()
    return { ok: true }
  } catch {
    return { ok: false }
  }
}

export const resolveTryItOutcome = (
  result: ResCenterMigrationTryItResult,
  fallbackResCenterUrl?: string | null,
): TryItOutcome => {
  const { status } = result

  if (status === 'existing_user') {
    const url = [result.state?.res_center_url, fallbackResCenterUrl].find(
      isSafeHttpUrl,
    )
    return url
      ? { kind: 'navigate', url }
      : { kind: 'error', errorKey: 'unavailable' }
  }

  if (status === 'sent' || status === 'already_sent') {
    return { kind: 'emailed', status }
  }

  return {
    kind: 'error',
    errorKey: RECOVERABLE_STATUSES.has(status as ResCenterMigrationErrorKey)
      ? (status as ResCenterMigrationErrorKey)
      : 'generic',
  }
}

/** `lock` is a ref-like object so a second submit is rejected before React re-renders. */
export const submitTryIt = async ({
  lock,
  tryIt,
  fallbackResCenterUrl,
  navigate,
}: {
  lock: { current: boolean }
  tryIt: () => Promise<ResCenterMigrationTryItResult>
  fallbackResCenterUrl?: string | null
  navigate: (url: string) => void
}): Promise<TryItOutcome> => {
  if (lock.current) return { kind: 'ignored' }
  lock.current = true

  try {
    const outcome = resolveTryItOutcome(await tryIt(), fallbackResCenterUrl)
    if (outcome.kind === 'navigate') navigate(outcome.url)
    return outcome
  } catch {
    return { kind: 'error', errorKey: 'generic' }
  } finally {
    lock.current = false
  }
}
